import useAsyncFn from 'react-use/lib/useAsyncFn';
import { unreachable } from '../unreachable';
import { getCampusTriggerByModelTag, getModelTag } from './utils';
export class GptStreamError extends Error {
  constructor(message) {
    super(message);
    this.name = 'GptStreamError';
  }
}
export const useStreamPrediction = _ref => {
  let {
    aiAssistantProps,
    messageType,
    setStreamingData
  } = _ref;
  const [state, getPrediction] = useAsyncFn(async () => {
    try {
      if (aiAssistantProps === undefined) {
        // unreachable: should only be called when aiAssistantProps is defined,
        // but we need to check it to satisfy the type checker because we need
        // to make it optional in the props for legacy reasons
        return;
      }
      const {
        exerciseId,
        generatePrediction,
        getIsExperiment,
        isProductionEnvironment,
        modelParams
      } = aiAssistantProps;
      const isExperiment = getIsExperiment();
      const modelTag = getModelTag(isProductionEnvironment, messageType, isExperiment);
      const triggerTag = getCampusTriggerByModelTag(modelTag);
      const response = await generatePrediction({
        body: {
          input: modelParams,
          shouldStream: true,
          simulateStreamDelay: 30,
          trigger: {
            _tag: triggerTag,
            exerciseId
          },
          useCache: true
        },
        params: {
          modelTag
        }
      });
      if (response.body != null && response.ok) {
        const reader = response.body.getReader();

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const {
            done,
            value
          } = await reader.read();

          // Stream is done, use previousValue to determine exit type
          if (done) {
            setStreamingData(previousValue => {
              switch (previousValue.type) {
                case 'streaming':
                  if (previousValue.content == null || previousValue.id == null) {
                    return {
                      reason: 'failed-to-get-gpt-response',
                      type: 'error'
                    };
                  }
                  return {
                    content: previousValue.content,
                    id: previousValue.id,
                    type: 'ready'
                  };
                case 'initial':
                case 'ready':
                case 'error':
                  return previousValue;
                default:
                  return unreachable(previousValue);
              }
            });
            break;
          }
          if (value) {
            const lines = new TextDecoder('utf-8').decode(value).split('\n');
            lines.forEach(line => {
              if (line.trim() !== '') {
                const gptStream = JSON.parse(line);
                if (gptStream.type === 'gpt-error') {
                  throw new GptStreamError(gptStream.reason);
                }
                setStreamingData(previousValue => {
                  switch (previousValue.type) {
                    case 'error':
                    case 'initial':
                      return {
                        content: gptStream.type === 'gpt-partial-answer' ? gptStream.choices[0].text : undefined,
                        id: gptStream.type === 'gpt-id' ? gptStream.id : undefined,
                        type: 'streaming'
                      };
                    case 'ready':
                      return previousValue;
                    case 'streaming':
                      {
                        const newContent = gptStream.type === 'gpt-partial-answer' ? "".concat(previousValue.content ? previousValue.content : '').concat(gptStream.choices[0].text) : previousValue.content;
                        return {
                          content: newContent,
                          id: previousValue.id,
                          type: 'streaming'
                        };
                      }
                    default:
                      return unreachable(previousValue);
                  }
                });
              }
            });
          }
        }
      } else {
        setStreamingData({
          reason: 'failed-to-get-gpt-response',
          type: 'error'
        });
      }
    } catch (error) {
      setStreamingData({
        // @ts-expect-error fix later
        reason: error.message,
        type: 'error'
      });
    }
  }, [aiAssistantProps]);
  return {
    getPrediction,
    loading: state.loading
  };
};